import { useEffect, useMemo, useState } from 'react'
import posthog from 'posthog-js'
import { useNavigate } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import { IoClose, IoReceiptOutline, IoSearch } from 'react-icons/io5'
import Header from 'components/headers/funds'
import SideBar from 'components/sidebar'
import SideBarItems from 'components/sidebar/items'
import DotsLoading from 'components/loadings/dotsLoading'
import CustomBottomSheet from 'components/bottomSheet'
import SortPill from 'components/sortPill'
import { SectionError } from 'components/states'
import { describeDay } from 'modules/fund/tabs/transactions/days'
import useGetTransactions from './hooks/useGetTransactions'
import useMemberId from './hooks/useMemberId'
import {
    describeTransaction,
    linkWalletRows,
    PERIOD_OPTIONS,
    periodParam,
    periodRange,
    periodRangeLabel,
    periodTotals,
    runsByDay,
    TPeriod,
} from './helpers'
import Item from './item'
import TransactionDetails from './details'
import PeriodSummary from './summary'

// The fund tab's pause: long enough that a name being typed is one request,
// short enough that the list answers before anyone wonders whether it heard.
const SEARCH_DEBOUNCE_MS = 450

// How far a period's summary reads ahead on its own: ten pages, three hundred
// payments — a year of a busy member's boxes. Past it the summary says what it
// was built from instead of reading on forever.
const SUMMARY_MAX_PAGES = 10

function ListSkeleton() {
    return (
        <div role="status" aria-live="polite">
            <span className="sr-only">در حال دریافت تراکنش‌ها</span>
            <div aria-hidden className="animate-pulse">
                {[4, 2].map((count, day) => (
                    <div key={day}>
                        <div className="mx-5 mb-2.5 mt-5 h-3 w-28 rounded bg-divider" />
                        <div className="mx-4 divide-y divide-divider overflow-hidden rounded-card border border-[#E4E7EB] bg-white">
                            {Array.from({ length: count }, (_, row) => (
                                <div
                                    key={row}
                                    className="flex items-center gap-2.5 px-3 py-3.5"
                                >
                                    <div className="h-9 w-9 shrink-0 rounded-[10px] bg-divider" />
                                    <div className="min-w-0 flex-1">
                                        <div className="h-3.5 w-32 rounded bg-divider" />
                                        <div className="mt-2 h-3 w-20 rounded bg-divider" />
                                    </div>
                                    <div className="h-3.5 w-20 rounded bg-divider" />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default function TransactionsPage() {
    const navigate = useNavigate()
    const { id: me, isLoading: isResolvingMe } = useMemberId()

    const [searchText, setSearchText] = useState('')
    const [appliedSearch, setAppliedSearch] = useState('')
    const [period, setPeriod] = useState<TPeriod>('')
    const [openPk, setOpenPk] = useState<number | null>(null)

    const date = useMemo(() => periodParam(period), [period])
    const range = useMemo(() => periodRange(period), [period])

    const {
        data,
        isLoading,
        isError,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useGetTransactions(appliedSearch, date)

    useEffect(() => {
        posthog.capture('my_transactions_viewed')
    }, [])

    useEffect(() => {
        const handle = setTimeout(
            () => setAppliedSearch(searchText.trim()),
            SEARCH_DEBOUNCE_MS
        )
        return () => clearTimeout(handle)
    }, [searchText])

    // Pages are fetched at different moments, so a payment made while the
    // list is open pushes a row from the end of one page onto the start of
    // the next. Seen twice, it is shown once.
    const rows = useMemo(() => {
        if (!data) return undefined
        const seen = new Set<number>()
        return data.pages
            .flatMap((page) => page?.results ?? [])
            .filter((row) => {
                if (seen.has(row.pk)) return false
                seen.add(row.pk)
                return true
            })
    }, [data])

    const days = useMemo(() => (rows ? runsByDay(rows) : []), [rows])

    const pageCount = data?.pages.length ?? 0
    const isCounting =
        period !== '' &&
        !!hasNextPage &&
        pageCount < SUMMARY_MAX_PAGES &&
        !isError
    const summaryCapped =
        period !== '' && !!hasNextPage && pageCount >= SUMMARY_MAX_PAGES

    // A period's summary is only true once every row of it is in, so it reads
    // ahead on its own rather than waiting for a scroll to the bottom.
    useEffect(() => {
        if (!isCounting || isFetchingNextPage) return
        fetchNextPage()
    }, [isCounting, isFetchingNextPage, pageCount, fetchNextPage])

    // Which rows are the two halves of one gateway payment — a top-up and
    // the payment it was handed straight on to. Worked out over the whole
    // list, so a pair split across two pages is still seen once both are in.
    const links = useMemo(
        () => (rows ? linkWalletRows(rows, me) : null),
        [rows, me]
    )

    const totals = useMemo(
        () =>
            rows && period !== ''
                ? periodTotals(rows, me, links ?? undefined)
                : null,
        [rows, period, me, links]
    )

    // How many of the period's top-ups were spent on the spot, for the note
    // under the summary that says they are not counted twice.
    const passThroughCount = useMemo(() => {
        if (!links) return 0
        let count = 0
        links.forEach((link) => {
            if (link.spentOn) count += 1
        })
        return count
    }, [links])

    // Read from the live list, not remembered at tap time: the sheet stays
    // open across a refetch.
    const openItem = useMemo(
        () =>
            openPk !== null && rows
                ? rows.find((row) => row.pk === openPk)
                : undefined,
        [openPk, rows]
    )

    // What a search found. The text never leaves the device — only how long
    // it was and whether it matched anything.
    const firstPageLength = data?.pages?.[0]?.results?.length
    useEffect(() => {
        if (appliedSearch === '' || firstPageLength === undefined) return
        posthog.capture('my_transactions_searched', {
            query_length: appliedSearch.length,
            result_count: firstPageLength,
            is_empty: firstPageLength === 0,
            period: period || null,
        })
        // Keyed on the search and the size of its answer, not on the page
        // identity, which changes on every background refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedSearch, firstPageLength])

    useEffect(() => {
        if (!isError) return
        posthog.capture('my_transactions_load_failed', {
            has_search: appliedSearch !== '',
            period: period || null,
            has_rows: !!rows?.length,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError])

    const choosePeriod = (next: TPeriod) => {
        if (next === period) return
        posthog.capture('my_transactions_period_filtered', {
            period: next || null,
            has_search: appliedSearch !== '',
        })
        setPeriod(next)
    }

    const loadMore = () => {
        if (isFetchingNextPage) return
        posthog.capture('my_transactions_page_loaded', {
            page: pageCount + 1,
            has_search: appliedSearch !== '',
            period: period || null,
        })
        fetchNextPage()
    }

    const openRow = (item: IDashboardTransactionResult) => {
        const link = links?.get(item.pk)
        posthog.capture('my_transactions_detail_opened', {
            kind: describeTransaction(item, me, link).kind,
            ctx_type: item.ctx_type,
            source: item.source,
            destination: item.destination,
            // Whether the row they opened is one half of a pass-through: the
            // pair is what «−۵,۰۰۰ در کنار +۵,۰۰۰» asks about.
            is_pass_through: !!link,
        })
        setOpenPk(item.pk)
    }

    const isSearching = appliedSearch !== ''
    const hasPeriod = period !== ''
    const periodLabel =
        PERIOD_OPTIONS.find((option) => option.key === period)?.label ?? ''

    const emptyText =
        isSearching && hasPeriod
            ? 'تراکنشی با این جستجو در این بازه پیدا نشد.'
            : isSearching
            ? 'تراکنشی در صندوقی با این نام پیدا نشد.'
            : hasPeriod
            ? 'در این بازه تراکنشی ندارید.'
            : ''

    return (
        <>
            <Header title="تراکنش‌ها" />
            <SideBar>
                <SideBarItems />
            </SideBar>

            <div className="min-h-screen bg-white pb-6">
                <div className="px-4 pt-3">
                    <label className="flex h-10 min-w-0 items-center gap-2 rounded-[10px] bg-sunken px-3 ring-1 ring-transparent transition-shadow focus-within:ring-green-1">
                        <IoSearch
                            aria-hidden
                            className="h-[18px] w-[18px] shrink-0 text-text-secondary"
                        />
                        <input
                            type="search"
                            enterKeyHint="search"
                            aria-label="جستجوی تراکنش‌ها با نام صندوق"
                            className="min-w-0 flex-1 text-ellipsis bg-transparent text-sm text-black outline-none placeholder:text-[13px] placeholder:text-text-muted [&::-webkit-search-cancel-button]:hidden"
                            placeholder="جستجوی نام صندوق"
                            value={searchText}
                            onChange={(event) =>
                                setSearchText(event.target.value)
                            }
                            onKeyDown={(event) => {
                                if (event.key !== 'Enter') return
                                setAppliedSearch(searchText.trim())
                                event.currentTarget.blur()
                            }}
                        />
                        {searchText !== '' && (
                            <button
                                type="button"
                                aria-label="پاک کردن جستجو"
                                onClick={() => {
                                    setSearchText('')
                                    setAppliedSearch('')
                                }}
                                className="-ml-1 shrink-0 p-1 text-text-secondary"
                            >
                                <IoClose className="h-4 w-4" />
                            </button>
                        )}
                    </label>
                </div>

                {/* Always on screen, not behind a filter button: which months
                    are shown is what makes the list read as the whole of it
                    or as a part. */}
                <div
                    role="radiogroup"
                    aria-label="بازهٔ زمانی"
                    className="flex gap-2 overflow-x-auto px-4 pb-0.5 pt-2.5"
                    style={{ scrollbarWidth: 'none' }}
                >
                    {PERIOD_OPTIONS.map((option) => (
                        <SortPill
                            key={option.key || 'all'}
                            checked={period === option.key}
                            onChange={() => choosePeriod(option.key)}
                            label={option.label}
                            aria={option.aria}
                        />
                    ))}
                </div>

                {totals && range && rows && rows.length > 0 && (
                    <PeriodSummary
                        title={periodLabel}
                        range={periodRangeLabel(range)}
                        totals={totals}
                        isCounting={isCounting}
                        cappedAt={summaryCapped ? rows.length : null}
                        isPartial={period !== '' && !!hasNextPage && isError}
                        passThroughCount={passThroughCount}
                    />
                )}

                {isError && !rows ? (
                    <div className="mx-4 mt-5">
                        <SectionError
                            message="تراکنش‌ها دریافت نشد"
                            onRetry={() => refetch()}
                        />
                    </div>
                ) : isLoading || isResolvingMe || !rows ? (
                    <ListSkeleton />
                ) : rows.length === 0 ? (
                    emptyText ? (
                        <p className="mt-10 px-6 text-center text-sm leading-6 text-text-secondary">
                            {emptyText}
                        </p>
                    ) : (
                        <div className="mt-12 flex flex-col items-center px-8 text-center">
                            <span
                                aria-hidden
                                className="flex h-14 w-14 items-center justify-center rounded-full bg-sunken text-text-secondary"
                            >
                                <IoReceiptOutline className="h-7 w-7" />
                            </span>
                            <p className="mb-0 mt-4 text-[15px] font-bold text-black">
                                هنوز تراکنشی ندارید
                            </p>
                            <p className="mb-0 mt-1.5 text-[13px] leading-6 text-text-secondary">
                                پرداخت‌های شما در صندوق‌ها، شارژ کیف پول و
                                ارتقای صندوق اینجا دیده می‌شود.
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate('/funds')}
                                className="mt-4 h-10 rounded-[10px] bg-brand-soft px-5 text-sm font-bold text-brand-deep"
                            >
                                دیدن صندوق‌ها
                            </button>
                        </div>
                    )
                ) : (
                    <InfiniteScroll
                        next={loadMore}
                        hasMore={!!hasNextPage && !isError}
                        dataLength={rows.length}
                        loader={<DotsLoading />}
                        // The library's wrapper is `overflow: auto`, which
                        // would make IT the box the sticky day headings stick
                        // to — and it never scrolls, so they never would.
                        style={{ overflow: 'visible' }}
                    >
                        {days.map((day, index) => {
                            const { title, date: when } = describeDay(day.key)
                            return (
                                <section
                                    key={`${day.key}-${index}`}
                                    aria-label={`${title} ${when}`}
                                >
                                    <h3 className="sticky top-14 z-[5] m-0 flex items-baseline gap-1.5 bg-white px-5 pb-1.5 pt-4 text-xs">
                                        <span className="font-bold text-black">
                                            {title}
                                        </span>
                                        <span className="text-text-secondary">
                                            {when}
                                        </span>
                                    </h3>
                                    <div className="mx-4 divide-y divide-divider overflow-hidden rounded-card border border-[#E4E7EB] bg-white">
                                        {day.rows.map((item) => (
                                            <Item
                                                key={item.pk}
                                                item={item}
                                                view={describeTransaction(
                                                    item,
                                                    me,
                                                    links?.get(item.pk)
                                                )}
                                                onOpen={() => openRow(item)}
                                            />
                                        ))}
                                    </div>
                                </section>
                            )
                        })}
                    </InfiniteScroll>
                )}

                {/* A page after the first that failed: the rows already
                    shown stay, and the rest can be asked for again. */}
                {isError && rows && rows.length > 0 && (
                    <div className="mx-4 mt-4 flex items-center justify-between gap-2 rounded-[10px] bg-sunken px-3 py-2.5 text-[13px]">
                        <p className="mb-0 text-text-secondary">
                            ادامهٔ تراکنش‌ها دریافت نشد
                        </p>
                        <button
                            type="button"
                            onClick={() =>
                                hasNextPage ? fetchNextPage() : refetch()
                            }
                            className="shrink-0 font-bold text-green-1"
                        >
                            تلاش مجدد
                        </button>
                    </div>
                )}
            </div>

            <CustomBottomSheet
                onDismiss={() => setOpenPk(null)}
                isOpen={!!openItem}
            >
                {openItem && (
                    <TransactionDetails
                        key={openItem.pk}
                        item={openItem}
                        view={describeTransaction(
                            openItem,
                            me,
                            links?.get(openItem.pk)
                        )}
                        me={me}
                        link={links?.get(openItem.pk)}
                        onClose={() => setOpenPk(null)}
                    />
                )}
            </CustomBottomSheet>
        </>
    )
}
